import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Send, Sparkles, CheckCircle, Mail, Phone, MapPin, HelpCircle 
} from 'lucide-react';
import { ParticleBackground } from './animations/ParticleBackground';
import { SectionWrapper } from './shared/SectionWrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Here you would normally send the data to your backend
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Show success animation
      setShowSuccess(true);
      toast.success('Message sent successfully!', {
        description: "We'll get back to you within 24 hours.",
      });

      // Reset form
      setFormState({
        name: '',
        email: '',
        message: ''
      });

      // Reset success state after animation
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);

    } catch (error) {
      toast.error('Failed to send message', {
        description: 'Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success Animation Component
  const SuccessAnimation = () => (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-r from-primary-400/20 to-primary-600/20 p-8 rounded-2xl
                   backdrop-blur-lg border border-white/10 shadow-xl"
        initial={{ scale: 0.8, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 20 }}
      >
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <PartyPopper className="h-12 w-12 text-primary-400 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
          <p className="text-gray-300">We'll get back to you soon</p>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  // Updated contact info array without phone and location
  const contactInfo = [
    {
      icon: <Mail className="h-6 w-6 text-primary-400" />,
      title: "Email Us",
      content: "hello@careerquest.ai",
      link: "mailto:hello@careerquest.ai",
      description: "We'll respond within 24 hours"
    }
  ];

  // FAQ Section
  const faqs = [
    {
      question: "How does the coin system work?",
      answer: "Career coins are our in-app currency that you can earn through daily logins, completing tasks, and subscribing to paid plans. Use them for premium features like AI reviews and mock interviews."
    },
    {
      question: "What's included in the free plan?",
      answer: "The free plan includes basic AI career assessment, 50 starter coins, and access to all features on a pay-per-use basis."
    },
    {
      question: "Can I upgrade or downgrade my plan?",
      answer: "Yes! You can change your plan at any time. Your coins and benefits will be adjusted accordingly."
    },
    {
      question: "How do I get started?",
      answer: "Simply create an account, complete your profile, and you'll receive your welcome coins to start exploring our features."
    }
  ];

  return (
    <SectionWrapper id="contact" className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        <ParticleBackground 
          color="#06b6d4"
          density={40}
          speed={0.3}
          connectParticles={true}
          connectionDistance={160}
          connectionOpacity={0.2}
        />
        <motion.div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.1) 0%, transparent 50%)',
          }}
          animate={{
            opacity: [0.3, 0.5, 0.3],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Success Animation */}
      <AnimatePresence>
        {showSuccess && <SuccessAnimation />}
      </AnimatePresence>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
            <MessageSquare className="h-8 w-8 text-primary-400" />
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Get in Touch
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll{' '}
            <motion.span className="relative inline-block">
              <span className="relative z-10 text-cyan-400 font-semibold">
                respond within 24 hours
              </span>
              <motion.span
                className="absolute inset-0 -z-10 blur-xl bg-cyan-400/20"
                animate={{
                  opacity: [0.5, 0.8, 0.5],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    required
                    className="w-full bg-white/5 border-white/10 text-white"
                    value={formState.name}
                    onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    required
                    className="w-full bg-white/5 border-white/10 text-white"
                    value={formState.email}
                    onChange={(e) => setFormState(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    required
                    rows={4}
                    className="w-full bg-white/5 border-white/10 text-white"
                    value={formState.message}
                    onChange={(e) => setFormState(prev => ({ ...prev, message: e.target.value }))}
                  />
                </div>
                <Button
                  type="submit"
                  className={`w-full ${
                    isSubmitting || showSuccess
                      ? 'bg-primary-400 hover:bg-primary-500'
                      : 'bg-gradient-to-r from-primary-400 to-primary-600 hover:from-primary-500 hover:to-primary-700'
                  } text-white transition-all duration-300`}
                  disabled={isSubmitting}
                >
                  <motion.div 
                    className="flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.05 }}
                  >
                    {isSubmitting ? (
                      <motion.div
                        className="h-5 w-5 border-2 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    ) : showSuccess ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                    {isSubmitting ? 'Sending...' : showSuccess ? 'Sent!' : 'Send Message'}
                  </motion.div>
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info & FAQ */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Email Contact Card */}
            {contactInfo.map((item, index) => (
              <motion.a
                key={index}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10
                         hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-primary-400/10 rounded-lg">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                    <p className="text-gray-300">{item.content}</p>
                    <p className="text-sm text-primary-400 mt-1">{item.description}</p>
                  </div>
                </div>
              </motion.a>
            ))}

            {/* FAQ Section */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <HelpCircle className="h-6 w-6 text-primary-400" />
                Frequently Asked Questions
              </h3>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                  >
                    <h4 className="text-lg font-semibold text-white mb-2">{faq.question}</h4>
                    <p className="text-gray-300">{faq.answer}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </SectionWrapper>
  );
}